import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from './Footer';
import ChatBox from './ChatBox';
import colors from '../theme/colors';

// Menu Configuration
import menuItems from '../config/menuItems';

const drawerWidth = 240;

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifMenuOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifMenuClose = () => {
    setNotifAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    // Clear authentication
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    // Navigate to signin page
    navigate('/signin');
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', background: colors.sidebar.background, minHeight: '100vh', borderRight: `1px solid ${colors.sidebar.border}` }}>
      <Box sx={{ p: 2.5, pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${colors.sidebar.logoBgStart} 0%, ${colors.sidebar.logoBgEnd} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${colors.overlay.medium}`,
            }}
          >
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>
              H
            </Typography>
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#0F172A', 
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Hive Identity
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#64748B',
                fontWeight: 500,
                fontSize: '0.7rem',
              }}
            >
              Admin Dashboard
            </Typography>
          </Box>
        </Stack>
      </Box>
      
      <Divider sx={{ borderColor: colors.sidebar.border, mx: 1.5 }} />
      
      <List sx={{ flex: 1, px: 2, pt: 2, overflow: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {menuItems.map((item, index) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&.Mui-selected': {
                  backgroundColor: colors.sidebar.selectedBg,
                  borderLeft: `3px solid ${colors.sidebar.selectedBorder}`,
                  '&:hover': {
                    backgroundColor: colors.sidebar.selectedBgHover,
                  },
                  '& .MuiListItemIcon-root': {
                    color: colors.sidebar.iconActive,
                    transform: 'scale(1.05)',
                  },
                  '& .MuiListItemText-primary': {
                    color: colors.sidebar.textActive,
                    fontWeight: 700,
                  },
                },
                '&:hover': {
                  backgroundColor: colors.sidebar.hover,
                },
                py: 0.75,
                px: 1.2,
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path ? colors.sidebar.iconActive : colors.sidebar.iconDefault,
                  minWidth: 36,
                  transition: 'all 0.2s ease-in-out',
                  '& svg': {
                    fontSize: '1.3rem',
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.8rem',
                  fontWeight: location.pathname === item.path ? 600 : 500,
                  color: location.pathname === item.path ? colors.sidebar.textActive : colors.sidebar.text,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Fixed Logout Button */}
      <Box sx={{ p: 1.5, borderTop: `1px solid ${colors.sidebar.border}` }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1.5,
            transition: 'all 0.2s ease-in-out',
            backgroundColor: '#FEF2F2',
            '&:hover': {
              backgroundColor: '#FEE2E2',
            },
            py: 0.9,
            px: 1.2,
          }}
        >
          <ListItemIcon 
            sx={{ 
              color: colors.error.main,
              minWidth: 36,
              '& svg': {
                fontSize: '1.3rem',
              },
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: colors.error.main,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: '#6366F1',
          borderBottom: 'none',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              color: 'white',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                color: 'white',
                letterSpacing: '-0.01em',
                fontSize: '1rem',
              }}
            >
              {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <motion.div
              animate={{
                rotate: [0, -15, 15, -15, 15, 0],
              }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                repeatDelay: 3,
              }}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <IconButton 
                onClick={handleNotifMenuOpen}
                sx={{ 
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                }}
              >
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </motion.div>
            <IconButton onClick={handleProfileMenuOpen}>
              <Avatar 
                sx={{ 
                  width: 34, 
                  height: 34,
                  background: 'white',
                  color: '#6366F1',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                A
              </Avatar>
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Notification Menu */}
      <Menu
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={handleNotifMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 360,
            maxHeight: 'none',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'visible',
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={700}>Notifications</Typography>
            <Typography variant="caption" color="text.secondary">You have 3 unread messages</Typography>
          </Box>
        </motion.div>
        <Box sx={{ 
          maxHeight: 280, 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {[
            { title: 'New project created', desc: 'Project "Alpha" was created', time: '5 min ago', unread: true },
            { title: 'Company updated', desc: 'Company "Tech Corp" details updated', time: '1 hour ago', unread: true },
            { title: 'Device registered', desc: 'New IoT device registered successfully', time: '2 hours ago', unread: true },
            { title: 'Alert resolved', desc: 'Critical alert #1234 has been resolved', time: '1 day ago', unread: false },
          ].map((notif, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <MenuItem 
                onClick={handleNotifMenuClose}
                sx={{ 
                  py: 1.5, 
                  px: 2,
                  borderBottom: index < 3 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  backgroundColor: notif.unread ? 'action.hover' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: notif.unread ? 'action.selected' : 'action.hover',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={notif.unread ? 600 : 500}>
                      {notif.title}
                    </Typography>
                    {notif.unread && (
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [1, 0.8, 1],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                        }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />
                      </motion.div>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {notif.desc}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                    {notif.time}
                  </Typography>
                </Box>
              </MenuItem>
            </motion.div>
          ))}
        </Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ 
                cursor: 'pointer', 
                fontWeight: 600, 
                '&:hover': { textDecoration: 'underline' },
                transition: 'all 0.2s',
              }}
              onClick={handleNotifMenuClose}
            >
              View all notifications
            </Typography>
          </Box>
        </motion.div>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={600}>Karthik</Typography>
          <Typography variant="caption" color="text.secondary">karthik@arthdesignbuild.com</Typography>
        </Box>
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation menu"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 60 } }} />
        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </Box>
        <Footer />
      </Box>
      <ChatBox />
    </Box>
  );
}

export default Layout;